import { Album, SortByQueryOptions } from '@bpm-web-app/download-api-sdk';
import { getFollowedPlaylists, useFeed, useFollowedArtists, useGetMeSubscription } from '@bpm-web-app/swr-hooks';
import { useSupremeFilterParams, useUserSettings } from '@bpm-web-app/utils';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import { useContext, useEffect, useMemo } from 'react';
import { LibraryTabsContext } from '../../../../utils/src/lib/library-tabs.context';
import { AppLink, CardCarousel, EmptyState, SecondaryPageTitle } from '../shared';
import { PlaylistCard } from '../shared/card/playlist-card/playlist-card';
import { usePagination } from '../shared/paging/paging';
import { TrackListFilters } from '../shared/track-list/track-list-filters/track-list-filters';
import { TrackListSupreme } from '../shared/track-list/supreme/track-list-supreme';
import { TrackListPresetSupreme } from '../shared/track-list/supreme/track-list-supreme-helpers';
import { ReactComponent as PlaylistEmptyIcon } from '../../assets/icons/playlist-empty.svg';
import { ReactComponent as TracksFeedEmptyIcon } from '../../assets/icons/tracks-feed-empty.svg';
import styles from './your-feed.module.css';

dayjs.extend(isToday);
dayjs.extend(isYesterday);

const getAddedDate = (album: Album) => album.highlighted_date || album.created_at;

const getTimeframeGroupKey = (date?: string) => {
    if (!date) return 'Earlier';
    const day = dayjs(date);
    const now = dayjs();

    if (day.isToday()) return 'Today';
    if (day.isYesterday()) return 'Yesterday';
    if (day.isSame(now, 'week')) return 'Earlier this Week';
    if (day.isSame(now.subtract(1, 'week'), 'week')) return 'Last Week';
    if (day.isSame(now, 'month')) return 'Earlier this Month';
    if (day.isSame(now.subtract(1, 'month'), 'month')) return 'Last Month';
    return 'Earlier';
};

export function YourFeed() {
    const { libraryProperty } = useContext(LibraryTabsContext);
    const { isAnonymous } = useUserSettings();
    const { data: subscriptionData } = useGetMeSubscription('supreme');
    const hasMembership = useMemo(() => subscriptionData?.data?.membership?.has_membership, [subscriptionData?.data?.membership?.has_membership]);

    /* Feed is a paid-only feature — anonymous and free users get nothing rendered, regardless
     * of what useFollowedArtists/fetchAlbumsForFollowedArtists below resolve to. */
    const isFeedLocked = isAnonymous || !hasMembership;

    const { data: followedArtistsData, isLoading: isLoadingFollowedArtists } = useFollowedArtists(libraryProperty);

    /* useFollowedArtists has no pagination — this passes every followed artist's slug into
     * one query. Fine for now; revisit if a user following hundreds of artists becomes an issue. */
    const followedArtistSlugs = useMemo(
        () => (isFeedLocked ? [] : followedArtistsData?.data?.map((artist) => artist.slug) || []),
        [followedArtistsData, isFeedLocked]
    );

    const { data: followedPlaylistsData, isLoading: isLoadingFollowedPlaylists } = getFollowedPlaylists(libraryProperty);

    /* getFollowedPlaylists has no sort param — sort by most recently updated client-side. */
    const sortedFollowedPlaylists = useMemo(
        () => (isFeedLocked ? [] : (followedPlaylistsData?.data || []).slice().sort((a, b) => dayjs(b.updated_at).diff(dayjs(a.updated_at)))),
        [followedPlaylistsData, isFeedLocked]
    );

    const { limit, page, bottomComponent, setPagination } = usePagination();

    /* artist is locked to the followed-artist list — not user-editable like the other filters */
    const defaultFilters = useMemo(() => ({ artist: followedArtistSlugs }), [followedArtistSlugs]);

    /* The feed no longer filters by artist
     * client-side — the backend derives the follow set from the session
     */
    const baseQuery = useSupremeFilterParams(true);
    const feedQuery = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { artist, ...filters } = baseQuery;
        return { ...filters, sort_by: SortByQueryOptions.DateDesc, limit, skip: (page - 1) * limit };
    }, [baseQuery, limit, page]);

    const shouldFetchAlbums = !isFeedLocked && followedArtistSlugs.length > 0;

    /* Server-paginated via GET /feed — one request per page; ordering and total are computed
     * server-side, so no client-side merge/dedupe/sort is needed. */
    const { data: feedData, mutate } = useFeed(feedQuery, shouldFetchAlbums);
    const isLoadingAlbums = shouldFetchAlbums && feedData === undefined;

    const pageAlbums = useMemo(() => feedData?.data || [], [feedData]);

    /* Split the current page's (already added-date-sorted) slice into contiguous Today/
     * Yesterday/Earlier this Week/Last Week/Earlier this Month/Last Month/Earlier sections. */
    const groupedByTimeframe = useMemo(() => {
        const groups: { key: string; items: Album[] }[] = [];
        pageAlbums.forEach((item) => {
            const key = getTimeframeGroupKey(getAddedDate(item));
            const lastGroup = groups[groups.length - 1];
            if (lastGroup?.key === key) {
                lastGroup.items.push(item);
            } else {
                groups.push({ key, items: [item] });
            }
        });
        return groups;
    }, [pageAlbums]);

    /* Reported even while loading/empty (total 0) so usePagination resets to page 1 when a filter
     * empties the list — same behavior as before, now sourced from the server pagination envelope. */
    const feedPagination = useMemo(
        () => ({
            total: feedData?.pagination?.total ?? 0,
            page,
            limited: limit,
            skipped: (page - 1) * limit,
        }),
        [feedData, page, limit]
    );

    useEffect(() => {
        setPagination(feedPagination);
    }, [feedPagination, setPagination]);

    const hasNoFollowedArtists = !isFeedLocked && !isLoadingFollowedArtists && followedArtistSlugs.length === 0;
    const isLoading = isLoadingFollowedArtists || isLoadingAlbums;

    /* One TrackListSupreme per timeframe section, each carrying the running index offset so the
     * count column stays continuous across sections and pages. While loading (or on a genuinely
     * empty page), fall back to a single empty section so TrackListSupreme still renders its
     * header/skeleton. */
    const timeframeSections = useMemo(() => {
        const sections = groupedByTimeframe.length > 0 ? groupedByTimeframe : [{ key: '', items: [] as Album[] }];
        let priorCount = (page - 1) * limit;

        return sections.map((section, sectionIndex) => {
            const indexOffset = priorCount;
            priorCount += section.items.length;

            return {
                key: section.key || 'loading',
                groupLabel: section.key,
                items: section.items,
                indexOffset,
                showHeader: sectionIndex === 0,
            };
        });
    }, [groupedByTimeframe, page, limit]);

    const playlistsTitle = (
        <span>
            From Your
            {' '}
            <AppLink href="favorites/playlists">
                <a className="underline-link">Followed Playlists</a>
            </AppLink>
        </span>
    );

    const artistsTitle = (
        <span>
            From Your
            {' '}
            <AppLink href="favorites/artists">
                <a className="underline-link">Followed Artists</a>
            </AppLink>
        </span>
    );

    return (
        <div className="spacing__window">
            <div className={styles['your-feed__list']}>
                {isFeedLocked ? null : (
                    <>
                        {!isLoadingFollowedPlaylists && (
                            sortedFollowedPlaylists.length > 0 ? (
                                <CardCarousel
                                    title={playlistsTitle}
                                    noPadding
                                    className={styles['your-feed__carousel']}
                                >
                                    {sortedFollowedPlaylists.map((item) => (
                                        <PlaylistCard key={item.id} playlist={item} playlists={sortedFollowedPlaylists} showUpdatedDate />
                                    ))}
                                </CardCarousel>
                            ) : (
                                <>
                                    <SecondaryPageTitle title={playlistsTitle} noPadding />
                                    <div className={styles['your-feed__empty-state']}>
                                        <EmptyState
                                            title="Playlist Feed"
                                            subtitle="Follow playlists to see when they're updated."
                                            illustration={<PlaylistEmptyIcon />}
                                            actionLabel="Explore Playlists"
                                            actionIcon="chevron-right-icon"
                                            href="exclusive-playlists"
                                            align="left"
                                            noPadding
                                        />
                                    </div>
                                </>
                            )
                        )}
                        <div className="spacing--top-double">
                            <SecondaryPageTitle title={artistsTitle} noPadding />
                        </div>
                        {hasNoFollowedArtists ? (
                            <div className={styles['your-feed__empty-state']}>
                                <EmptyState
                                    title="Artist Feed"
                                    subtitle="Follow artists to never miss a release."
                                    illustration={<TracksFeedEmptyIcon />}
                                    actionLabel="View Top Artists"
                                    actionIcon="chevron-right-icon"
                                    href="trending"
                                    align="left"
                                    noPadding
                                />
                            </div>
                        ) : (
                            <>
                                <div className="spacing--top">
                                    <TrackListFilters dynamicActiveTabColor showActiveFilters defaultFilters={defaultFilters} />
                                </div>
                                {timeframeSections.map((section) => (
                                    <TrackListSupreme
                                        key={section.key}
                                        preset={TrackListPresetSupreme.Download}
                                        list={section.items}
                                        isLoading={isLoading}
                                        isSortable={false}
                                        indexOffset={section.indexOffset}
                                        showHeader={section.showHeader}
                                        groupLabel={section.groupLabel}
                                        highlightRecentReleaseDate
                                        onDownloadRevalidate={(downloadMedia) => {
                                            if (downloadMedia) {
                                                // eslint-disable-next-line no-param-reassign
                                                downloadMedia.download_count += 1;
                                                mutate(feedData, false);
                                            }
                                        }}
                                    />
                                ))}
                                {feedPagination.total > limit ? bottomComponent : null}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default YourFeed;
